// Undo and redo (live's ⌘Z and ⇧⌘Z): the editor's timeline states as a history around the one
// reducer. An edit that changes nothing is not kept; a run of the same move, trim or setting on
// the same clip or transition (a drag's many moves, a slider, a caption's typing) is one step, while an add, a split, a duplicate,
// a separation or a removal is always its own; a load (a save's answer, a reload) starts afresh. Another
// save merged in (spec live-sync-merge.md) is no step: it reaches every step, so Undo never takes it back.
import { editTimeline, refusal, type Timeline, type TimelineEdit } from "./timeline";

/** The most steps kept back. */
export const MAX_STEPS = 100;
/** Edits of one kind on one clip this close together are one step. */
const RUN_MS = 600;

export interface History {
  past: readonly Timeline[];
  present: Timeline;
  future: readonly Timeline[];
  /** The last edit's kind, clip and time, to join a run (a null key joins nothing). */
  last: { key: string | null; at: number } | null;
}

export type HistoryAction =
  | { type: "edit"; edit: TimelineEdit; at: number }
  | { type: "undo" }
  | { type: "redo" }
  /** Another save merged in, `theirs` against `base` (spec live-sync-merge.md): no step of its own. */
  | { type: "sync"; base: Timeline; theirs: Timeline };

export function historyOf(timeline: Timeline): History {
  return { past: [], present: timeline, future: [], last: null };
}

/** The edits a drag or a slider sends many of; any other edit is a step of its own. */
const RUNS: ReadonlySet<TimelineEdit["type"]> = new Set([
  "moveClip",
  "trimStart",
  "trimEnd",
  "setRange",
  "setClip",
  "setTransitionDuration",
  // A Speed slider's run (spec clip-speed), and a marker's drag (spec markers). A curve's edit is a
  // step of its own: its tile, and a point's drag, made once when let go (spec clip-speed-curves-tab).
  "setSpeed",
  "setMarker",
  // The Volume slider's run at the playhead (spec volume-keyframes).
  "setVolumeAt",
  // Typing in a caption's Transcript box (#239): a run of keys is one step.
  "setCaptionWords",
]);

/** What a run joins on: the same continuous edit of the same clip; null for a step of its own. */
function keyOf(edit: TimelineEdit): string | null {
  // Several clips' run (a slider on a selection of them) runs as one.
  if (edit.type === "many") {
    if (edit.alone === true) return null;
    const keys = edit.edits.map(keyOf);
    return keys.length > 0 && keys.every((key) => key !== null) ? `many:${keys.join(",")}` : null;
  }
  return RUNS.has(edit.type) && "id" in edit ? `${edit.type}:${edit.id}` : null;
}

/**
 * Steps, the nearest first, each with another save merged in, up to the first that cannot take it:
 * it and those beyond go, since one left as it was would bring back a state without theirs.
 */
function synced(steps: readonly Timeline[], edit: TimelineEdit): Timeline[] {
  const taken: Timeline[] = [];
  for (const step of steps) {
    const next = editTimeline(step, edit);
    if (next === step && refusal(step, edit) !== null) break;
    taken.push(next);
  }
  return taken;
}

export function editHistory(history: History, action: HistoryAction): History {
  switch (action.type) {
    case "sync": {
      const edit: TimelineEdit = { type: "sync", base: action.base, theirs: action.theirs };
      const present = editTimeline(history.present, edit);
      // A present that cannot take it: the history as it was (the caller asks `refusal` in the
      // same reducer step, so nothing can come between).
      if (present === history.present && refusal(history.present, edit) !== null) return history;
      // The other steps keep only the present's unsaved values over theirs (spec live-sync-merge.md, decision 5).
      const steps: TimelineEdit = { ...edit, present: history.present };
      const past = synced([...history.past].reverse(), steps).reverse();
      return { past, present, future: synced(history.future, steps), last: history.last };
    }
    case "undo": {
      const previous = history.past.at(-1);
      if (previous === undefined) return history;
      return {
        past: history.past.slice(0, -1),
        present: previous,
        future: [history.present, ...history.future],
        last: null,
      };
    }
    case "redo": {
      const [next, ...rest] = history.future;
      if (next === undefined) return history;
      return {
        past: [...history.past, history.present],
        present: next,
        future: rest,
        last: null,
      };
    }
    case "edit": {
      const { edit, at } = action;
      if (edit.type === "load") return historyOf(edit.timeline);
      const present = editTimeline(history.present, edit);
      if (present === history.present) return history;
      const key = keyOf(edit);
      const joins =
        key !== null && history.last?.key === key && at - history.last.at < RUN_MS;
      const past = joins ? history.past : [...history.past, history.present].slice(-MAX_STEPS);
      return { past, present, future: [], last: { key, at } };
    }
    default:
      return action satisfies never;
  }
}
